import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  useTheme,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Modal,
} from "@mui/material";
import PaymentsIcon from "@mui/icons-material/Payments";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { DataTable, Column } from "../../../shared";
import { useCompanyDividends, Dividend, isDividendNotFoundError } from "../hooks/useCompanyDividends";
import {
  getFlatPortfolioColors,
  portfolioTypography,
} from "../../../../utils/getFlatPortfolioColors";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { roundedPanelSx } from "../../overviewPanel";

interface DividendHistoryTableProps {
  ticker: string;
  price: number;
}

export const DividendHistoryTable: React.FC<DividendHistoryTableProps> = ({
  ticker,
  price,
}) => {
  const theme = useTheme();
  const colors = getFlatPortfolioColors(theme);
  const { formatPrice } = useStockCurrency();
  const { data: dividends, isLoading: dividendsLoading, error } = useCompanyDividends(ticker, 100);
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const isLoading = dividendsLoading;

  // Check for next dividend payout
  const nextPayoutInfo = useMemo(() => {
    if (!dividends || dividends.length === 0) return null;

    // Filter out special dividends and get the latest
    const regularDividends = dividends.filter((d) => d.frequency !== "Special");
    const latestRegularDividend = regularDividends[0];
    const latestDividendDate = latestRegularDividend?.paymentDate ?? null;

    // Check if the latest dividend payment is in the future (declared but not yet paid)
    const isNextPaymentDeclared = latestDividendDate
      ? dayjs(latestDividendDate).isAfter(dayjs())
      : false;

    return isNextPaymentDeclared ? latestDividendDate : null;
  }, [dividends]);

  // Format date using dayjs
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "-";
    const date = dayjs(dateStr);
    return date.isValid() ? date.format("MMM D, YYYY") : "-";
  };

  // Format currency using the stock's currency context (handles GBX conversion)
  // Round down to 2 decimal places for readability
  const formatCurrency = (value: number | null | undefined): string => {
    if (value == null) return "-";
    const roundedDown = Math.floor(value * 100) / 100;
    return formatPrice(roundedDown, 2);
  };

  // Format percentage
  const formatPercent = (value: number | null | undefined): string => {
    if (value == null) return "-";
    return `${value.toFixed(2)}%`;
  };

  // Column definitions
  const columns: Column<Dividend>[] = [
    {
      key: "paymentDate",
      header: "Payment Date",
      sortable: true,
      render: (value) => (
        <Typography sx={{ ...portfolioTypography.body, color: colors.text.primary, fontWeight: 500 }}>
          {formatDate(value)}
        </Typography>
      ),
    },
    {
      key: "dividend",
      header: "Amount",
      sortable: true,
      align: "right",
      render: (value) => (
        <Typography sx={{ ...portfolioTypography.body, color: colors.text.primary, fontWeight: 600 }}>
          {formatCurrency(value)}
        </Typography>
      ),
    },
    {
      key: "yield",
      header: "Yield",
      sortable: false,
      align: "right",
      render: (_, row) => {
        const calculatedYield = price > 0 ? (row.dividend / price) * 100 : 0;
        return (
          <Typography
            sx={{
              ...portfolioTypography.body,
              color: calculatedYield > 0 ? colors.functional.success : colors.text.secondary,
              fontWeight: 600,
            }}
          >
            {formatPercent(calculatedYield)}
          </Typography>
        );
      },
    },
    {
      key: "frequency",
      header: "Frequency",
      sortable: false,
      align: "center",
      render: (value: Dividend["frequency"]) => (
        <Chip
          label={value}
          size="small"
          variant="outlined"
          sx={{
            fontSize: "0.75rem",
            borderRadius: 0,
            borderColor: colors.border.primary,
            color: colors.text.secondary,
          }}
        />
      ),
    },
    {
      key: "date",
      header: "Ex-Dividend",
      sortable: true,
      render: (value) => (
        <Typography sx={{ ...portfolioTypography.body, color: colors.text.secondary }}>
          {formatDate(value)}
        </Typography>
      ),
    },
    {
      key: "declarationDate",
      header: "Declaration",
      sortable: true,
      render: (value) => (
        <Typography sx={{ ...portfolioTypography.body, color: colors.text.secondary }}>
          {formatDate(value)}
        </Typography>
      ),
    },
  ];

  // Mobile card render
  const mobileCardRender = (dividend: Dividend) => (
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
        <Box>
          <Typography sx={{ ...portfolioTypography.body, color: colors.text.primary, fontWeight: 600 }}>
            {formatDate(dividend.paymentDate)}
          </Typography>
          <Typography sx={{ ...portfolioTypography.caption, color: colors.text.secondary }}>
            Payment Date
          </Typography>
        </Box>
        <Chip
          label={dividend.frequency}
          size="small"
          variant="outlined"
          sx={{
            fontSize: "0.7rem",
            borderRadius: 0,
            borderColor: colors.border.primary,
            color: colors.text.secondary,
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 3, mb: 1.5 }}>
        <Box>
          <Typography sx={{ ...portfolioTypography.body, color: colors.text.primary, fontWeight: 600 }}>
            {formatCurrency(dividend.dividend)}
          </Typography>
          <Typography sx={{ ...portfolioTypography.caption, color: colors.text.secondary }}>
            Amount
          </Typography>
        </Box>
        <Box>
          {(() => {
            const calculatedYield = price > 0 ? (dividend.dividend / price) * 100 : 0;
            return (
              <>
                <Typography
                  sx={{
                    ...portfolioTypography.body,
                    color: calculatedYield > 0 ? colors.functional.success : colors.text.secondary,
                    fontWeight: 600,
                  }}
                >
                  {formatPercent(calculatedYield)}
                </Typography>
                <Typography sx={{ ...portfolioTypography.caption, color: colors.text.secondary }}>
                  Yield
                </Typography>
              </>
            );
          })()}
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 3 }}>
        <Box>
          <Typography sx={{ ...portfolioTypography.caption, color: colors.text.secondary }}>
            Ex-Dividend: {formatDate(dividend.date)}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ ...portfolioTypography.caption, color: colors.text.secondary }}>
            Declaration: {formatDate(dividend.declarationDate)}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  );

  // Loading state
  if (isLoading) {
    return (
      <Card
        sx={{
          ...roundedPanelSx(theme.palette.mode === "dark"),
          p: 0,
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <PaymentsIcon sx={{ color: colors.functional.success, fontSize: 24 }} />
            <Typography sx={{ ...portfolioTypography.title, color: colors.text.primary }}>
              Dividend History
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} sx={{ color: colors.functional.success }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // No data state (empty data or 404)
  const isNoDividendData = (!dividends || dividends.length === 0) || isDividendNotFoundError(error);
  if (!isLoading && isNoDividendData) {
    return (
      <Card
        sx={{
          ...roundedPanelSx(theme.palette.mode === "dark"),
          p: 0,
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <PaymentsIcon sx={{ color: colors.functional.success, fontSize: 24 }} />
            <Typography sx={{ ...portfolioTypography.title, color: colors.text.primary }}>
              Dividend History
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <Typography sx={{ ...portfolioTypography.body, color: colors.text.secondary }}>
              This company does not currently pay dividends, or dividend data could not be loaded.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Error state (non-404 errors)
  if (error) {
    return (
      <Card
        sx={{
          ...roundedPanelSx(theme.palette.mode === "dark"),
          p: 0,
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <PaymentsIcon sx={{ color: colors.functional.success, fontSize: 24 }} />
            <Typography sx={{ ...portfolioTypography.title, color: colors.text.primary }}>
              Dividend History
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <Typography sx={{ ...portfolioTypography.body, color: colors.text.secondary }}>
              Unable to load dividend data
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // At this point, dividends should be defined and have data
  if (!dividends) return null;

  return (
    <Card
      sx={{
        ...roundedPanelSx(theme.palette.mode === "dark"),
        p: 0,
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 2,
            borderBottom: `1px solid ${colors.border.secondary}`,
          }}
        >
          <PaymentsIcon sx={{ color: colors.functional.success, fontSize: 24 }} />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={{ ...portfolioTypography.title, color: colors.text.primary }}>
                Dividend History
              </Typography>
              <IconButton
                size="small"
                onClick={() => setInfoModalOpen(true)}
                sx={{
                  color: colors.text.secondary,
                  p: 0.25,
                  "&:hover": {
                    color: colors.text.primary,
                    backgroundColor: "transparent",
                  },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <Typography sx={{ ...portfolioTypography.caption, color: colors.text.secondary }}>
              {dividends.length} payments
              {nextPayoutInfo && (
                <> · Next payout: {dayjs(nextPayoutInfo).format("MMM D, YYYY")}</>
              )}
            </Typography>
          </Box>
        </Box>

        {/* Table */}
        <DataTable
          data={dividends}
          columns={columns}
          pagination
          defaultPageSize={10}
          pageSizeOptions={[10, 25, 50]}
          sortable
          defaultSort={{ key: "paymentDate", direction: "desc" }}
          emptyMessage={`No dividend history available for ${ticker}`}
          mobileCardRender={mobileCardRender}
          getRowKey={(row, index) => `${row.symbol}-${row.paymentDate}-${index}`}
        />
      </CardContent>

      {/* Info Modal */}
      <Modal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        aria-labelledby="dividend-yield-info-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 420 },
            maxWidth: "calc(100vw - 32px)",
            backgroundColor: theme.palette.background.surface,
            border: `1px solid ${colors.border.primary}`,
            borderRadius: 0,
            p: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
            <Typography
              id="dividend-yield-info-title"
              sx={{ ...portfolioTypography.title, color: colors.text.primary }}
            >
              About Dividend Yield
            </Typography>
            <IconButton
              size="small"
              onClick={() => setInfoModalOpen(false)}
              sx={{ color: colors.text.secondary, p: 0.5 }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Typography
            sx={{ ...portfolioTypography.body, color: colors.text.secondary, mb: 2 }}
          >
            The yield shown for each dividend payment is calculated using the{" "}
            <strong style={{ color: colors.text.primary }}>current stock price</strong>,
            not the price at the time of distribution.
          </Typography>

          <Typography
            sx={{ ...portfolioTypography.body, color: colors.text.secondary }}
          >
            This approach provides a more relevant view for evaluating current dividend
            value, allowing you to see what yield each historical payment would represent
            if you bought the stock today.
          </Typography>
        </Box>
      </Modal>
    </Card>
  );
};

export default DividendHistoryTable;
